var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { v4 as uuidv4 } from "uuid";
import undoable, { includeAction } from "redux-undo";
import { createSlice } from "@reduxjs/toolkit";
import { NodeComponentType, GripPositionMarkerId, TubeType, PalletPatternId, PatternTransform, } from "./types.current";
import { addEdgeToState, defaultNodeWidth, findFreeCardboardBoxPosition, findFreeNodePosition, updateTubeTypes, } from "./helpers";
import { arePlacementsEquivalent, computePalletPattern, } from "3d/pallet-patterns";
import { getBoxWorldFootprint, getCardboardBoxWorldFootprint } from "3d/pallet";
import { computeGripFrame, gripFrameTurnDegrees, gripFrameTurnDirection, getNodesMountedOnGripFrame, turnAboutGripFrameCentre, } from "3d/grip-frame";
import { palletLayoutToBoxes } from "store/scene/pallet-layout";
import { gripPositionMarkerDefaultWidth, tubeConnectionDefaultDiameter, objectShapes, defaultObject, feedPressurePointDefaultX, feedPressurePointDefaultZ, centralizedVacuumPumpDefaultX, centralizedVacuumPumpDefaultZ, feedPressurePointNodeId, defaultInlineTubeLength, defaultInlineTubeDiameter, tubeConnectionModels, defaultOvalGripPositionDiameterRatio, objectPosition, defaultCardboardBoxId, newCardboardBoxOffset, defaultPalletSize, } from "3d/constants/common";
import { defaultObjectSize, palletBoxDefaultSize, } from "3d/constants/adjustable-shapes";
export var initialState = {
    nodes: (_a = {},
        _a[feedPressurePointNodeId] = {
            id: feedPressurePointNodeId,
            type: NodeComponentType.FEED_PRESSURE_POINT,
            component: {
                nodeId: feedPressurePointNodeId,
            },
            rotationY: 0,
            attachmentIds: [],
            position: {
                x: feedPressurePointDefaultX,
                y: objectShapes[defaultObject].tubeNetworkY,
                z: feedPressurePointDefaultZ,
            },
        },
        _a),
    attachments: {},
    edges: {},
    objectShape: defaultObject,
    objectSize: defaultObjectSize,
    cardboardBoxes: [
        {
            id: defaultCardboardBoxId,
            position: { x: objectPosition.x, z: objectPosition.z },
            rotation: 0,
            layer: 0,
        },
    ],
    pallet: __assign({}, defaultPalletSize),
    // A new system starts as the single centred box; the second row is empty
    // until a pattern is picked for it.
    layerPatterns: {
        0: { patternId: PalletPatternId.SINGLE, transform: PatternTransform.NONE },
        1: null,
    },
    // Working with a pallet of boxes is opt-in per system; until then a cardboard
    // system looks like every other application (see `setMultipleBoxesEnabled`).
    multipleBoxesEnabled: false,
    hasDisconnectedEdges: true,
};
export var initialStateWithHistory = {
    past: [],
    present: __assign({}, initialState),
    future: [],
};
var addGripPosition = function (state, action) {
    var _a, _b;
    var newNodePositionAndWidth = {
        position: {
            x: (_a = objectShapes[state.objectShape].gripPositionX) !== null && _a !== void 0 ? _a : 0,
            y: objectShapes[state.objectShape].gripPositionY,
            z: (_b = objectShapes[state.objectShape].gripPositionZ) !== null && _b !== void 0 ? _b : 0,
        },
        width: gripPositionMarkerDefaultWidth[GripPositionMarkerId.CIRCLE],
    };
    var freePos = findFreeNodePosition(newNodePositionAndWidth, Object.values(state.nodes), NodeComponentType.GRIP_POSITION);
    state.nodes[action.payload] = {
        id: action.payload,
        type: NodeComponentType.GRIP_POSITION,
        component: {
            nodeId: action.payload,
            marker: GripPositionMarkerId.CIRCLE,
            width: gripPositionMarkerDefaultWidth[GripPositionMarkerId.CIRCLE],
            inlineTubeLength: defaultInlineTubeLength,
            inlineTubeDiameter: defaultInlineTubeDiameter,
        },
        rotationY: 0,
        attachmentIds: [],
        position: freePos !== null && freePos !== void 0 ? freePos : newNodePositionAndWidth.position,
    };
};
var addTubeConnection = function (state, action) {
    var newNodePositionAndWidth = {
        position: {
            x: 0,
            y: objectShapes[state.objectShape].tubeNetworkY,
            z: 0,
        },
        width: defaultNodeWidth,
    };
    var freePos = findFreeNodePosition(newNodePositionAndWidth, Object.values(state.nodes), NodeComponentType.TUBE_CONNECTION);
    state.nodes[action.payload] = {
        id: action.payload,
        type: NodeComponentType.TUBE_CONNECTION,
        component: {
            nodeId: action.payload,
            tubeType: TubeType.UNKNOWN,
            diameters: [tubeConnectionDefaultDiameter],
        },
        attachmentIds: [],
        rotationY: 0,
        position: freePos !== null && freePos !== void 0 ? freePos : newNodePositionAndWidth.position,
    };
};
var addCentralizedPump = function (state, action) {
    var newNodePositionAndWidth = {
        position: {
            x: centralizedVacuumPumpDefaultX,
            y: objectShapes[state.objectShape].tubeNetworkY,
            z: centralizedVacuumPumpDefaultZ,
        },
        width: defaultNodeWidth,
    };
    var freePos = findFreeNodePosition(newNodePositionAndWidth, Object.values(state.nodes), NodeComponentType.CENTRALIZED_VACUUM_PUMP);
    state.nodes[action.payload] = {
        id: action.payload,
        type: NodeComponentType.CENTRALIZED_VACUUM_PUMP,
        component: {
            nodeId: action.payload,
        },
        rotationY: 0,
        attachmentIds: [],
        position: freePos !== null && freePos !== void 0 ? freePos : newNodePositionAndWidth.position,
    };
};
var addEdges = function (state, action) {
    var e_1, _a;
    var edges = action.payload;
    try {
        for (var edges_1 = __values(edges), edges_1_1 = edges_1.next(); !edges_1_1.done; edges_1_1 = edges_1.next()) {
            var edge = edges_1_1.value;
            addEdgeToState(state, edge);
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (edges_1_1 && !edges_1_1.done && (_a = edges_1.return)) _a.call(edges_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    updateTubeTypes(state);
};
var addEdge = function (state, action) {
    addEdgeToState(state, action.payload);
    updateTubeTypes(state);
};
/* Removes an edge and frees its attachments. Returns false for a missing edge,
 * e.g. one already removed together with its node. */
var removeEdge = function (state, edgeId) {
    var edge = state.edges[edgeId];
    if (!edge)
        return false;
    state.attachments[edge.fromAttachmentId].edgeId = undefined;
    state.attachments[edge.toAttachmentId].edgeId = undefined;
    delete state.edges[edgeId];
    return true;
};
/* Removes a node with its attachments and the edges connected to them.
 * Returns false (and changes nothing) for a missing node or the feed pressure
 * point, which can never be deleted. */
var removeNode = function (state, nodeId) {
    if (!state.nodes[nodeId] ||
        state.nodes[nodeId].type === NodeComponentType.FEED_PRESSURE_POINT)
        return false;
    state.nodes[nodeId].attachmentIds.forEach(function (attachmentId) {
        var edgeId = state.attachments[attachmentId].edgeId;
        if (edgeId)
            removeEdge(state, edgeId);
        delete state.attachments[attachmentId];
    });
    delete state.nodes[nodeId];
    return true;
};
var deleteNode = function (state, action) {
    if (removeNode(state, action.payload))
        updateTubeTypes(state);
};
/* Deletes several nodes and edges as ONE undo step, so a single undo brings
 * them all back (e.g. a multi-selection, the "remove empty grip positions"
 * prompt). */
var deleteEntities = function (state, action) {
    var _a = action.payload, nodeIds = _a.nodeIds, edgeIds = _a.edgeIds;
    var removedNodes = nodeIds.filter(function (nodeId) { return removeNode(state, nodeId); });
    var removedEdges = edgeIds.filter(function (edgeId) { return removeEdge(state, edgeId); });
    if (removedNodes.length > 0 || removedEdges.length > 0)
        updateTubeTypes(state);
};
var setNodeAttachments = function (state, action) {
    var _a = action.payload, placeholders = _a.placeholders, nodeId = _a.nodeId;
    var node = state.nodes[nodeId];
    if (!node)
        return;
    /*
     * Identity-stable fast path: if the requested placeholders already match the
     * node's current attachments (by placeholder name + tube type), keep the
     * existing attachment ids — and therefore their `edgeId` back-references and
     * the `fromAttachmentId`/`toAttachmentId` of any connected edges — and only
     * refresh the volatile entity name.
     *
     * The 3D node components re-dispatch this action on every remount (their
     * effects depend on the PlayCanvas `nodeEntity`), which happens whenever a
     * node is removed and re-added — e.g. while undoing/redoing. Regenerating the
     * attachment ids on every such remount would leave edges (whose endpoint refs
     * live in the undo history) pointing at stale, deleted attachments.
     */
    var currentAttachments = node.attachmentIds
        .map(function (attachmentId) { return state.attachments[attachmentId]; })
        .filter(function (attachment) { return Boolean(attachment); });
    var attachmentsMatchPlaceholders = currentAttachments.length === placeholders.length &&
        placeholders.every(function (placeholder) {
            return currentAttachments.some(function (attachment) {
                return attachment.placeholderName === placeholder.name &&
                    attachment.tubeType === placeholder.tubeType;
            });
        });
    if (attachmentsMatchPlaceholders) {
        var unmatched_1 = __spreadArray([], __read(currentAttachments), false);
        placeholders.forEach(function (placeholder) {
            var index = unmatched_1.findIndex(function (attachment) {
                return attachment.placeholderName === placeholder.name &&
                    attachment.tubeType === placeholder.tubeType;
            });
            if (index === -1)
                return;
            var attachment = unmatched_1[index];
            unmatched_1.splice(index, 1);
            /* Reassign as a NEW object (rather than just mutating the entity name)
             * so selectors recompute and edges connected to this node re-resolve
             * their 3D entities. This action fires when the node's model has just
             * been (re)built — e.g. after an undo/redo remount — so even when the
             * stored data is unchanged (pump/feed-pressure attachments carry no
             * entity name) the underlying PlayCanvas entities have changed and the
             * connected tubes must redraw against them. */
            state.attachments[attachment.id] = __assign(__assign({}, attachment), { placeholderEntityName: placeholder.entityName });
        });
        updateTubeTypes(state);
        return;
    }
    /*
     * Setting the attachments of a node means we create a new attachment
     * for each placeholder.
     */
    var newAttachments = placeholders.map(function (placeholder) {
        var attachment = {
            id: "attachment-".concat(uuidv4()),
            nodeId: nodeId,
            placeholderName: placeholder.name,
            tubeType: placeholder.tubeType,
            nodeType: node.type,
            placeholderEntityName: placeholder.entityName,
        };
        state.attachments[attachment.id] = attachment;
        return attachment;
    });
    /* The current edges should be mapped to the new attachments. */
    var updateEdge = function (edgeId, oldAttachmentId, newAttachmentId) {
        state.edges[edgeId].fromAttachmentId =
            state.edges[edgeId].fromAttachmentId === oldAttachmentId
                ? newAttachmentId
                : state.edges[edgeId].fromAttachmentId;
        state.edges[edgeId].toAttachmentId =
            state.edges[edgeId].toAttachmentId === oldAttachmentId
                ? newAttachmentId
                : state.edges[edgeId].toAttachmentId;
    };
    /* If possible, we map the edge to a new attachment with the same
     * tubetype or placeholder name as the old one. This is an simple way of making sure
     * tubes are attached to the same position as before when re-initializing a state */
    var oldAttachmentWithEdges = [];
    node.attachmentIds.forEach(function (attachmentId) {
        var oldAttachment = state.attachments[attachmentId];
        if (oldAttachment.edgeId) {
            var newAttachment = newAttachments.find(function (newAttachment) {
                var newTubeType = newAttachment.tubeType, newPlaceholderName = newAttachment.placeholderName;
                var oldTubeType = oldAttachment.tubeType, oldPlaceholderName = oldAttachment.placeholderName;
                var isTubeTypeMatch = newTubeType === oldTubeType;
                var isPlaceholderNameMatch = newPlaceholderName === oldPlaceholderName;
                return ((isTubeTypeMatch || isPlaceholderNameMatch) && !newAttachment.edgeId);
            });
            if (newAttachment) {
                newAttachment.edgeId = oldAttachment.edgeId;
                updateEdge(oldAttachment.edgeId, oldAttachment.id, newAttachment.id);
            }
            else {
                oldAttachmentWithEdges.push(oldAttachment);
            }
        }
    });
    /* For edges we could not map based on name, we map the edge to any new attachment
     * that does not have an edge already and that shares the same tubetype as the old attachment.*/
    oldAttachmentWithEdges.forEach(function (oldAttachment) {
        var edgeId = oldAttachment.edgeId; //We know this is not undefined
        var newAttachment = newAttachments.find(function (newAttachment) { return !newAttachment.edgeId; });
        var isTubeTypeMatching = newAttachment && (newAttachment === null || newAttachment === void 0 ? void 0 : newAttachment.tubeType) === oldAttachment.tubeType;
        if (isTubeTypeMatching) {
            newAttachment.edgeId = edgeId;
            updateEdge(edgeId, oldAttachment.id, newAttachment.id);
            /* If no new (available) attachment exists, we delete the edge */
        }
        else {
            var edgeToDelete = state.edges[edgeId];
            delete state.edges[edgeId];
            var otherAttachmentId = edgeToDelete.fromAttachmentId === oldAttachment.id
                ? edgeToDelete.toAttachmentId
                : edgeToDelete.fromAttachmentId;
            state.attachments[otherAttachmentId].edgeId = undefined;
        }
    });
    /* Delete all existing attachments */
    node.attachmentIds.forEach(function (attachmentId) {
        delete state.attachments[attachmentId];
    });
    node.attachmentIds = newAttachments.map(function (attachment) { return attachment.id; });
    updateTubeTypes(state);
};
var setNodePosition = function (state, action) {
    state.nodes[action.payload.nodeId].position = action.payload.position;
};
/*
 * Move the frame ("grimma") the cups are bolted to by a world XZ offset. Only
 * what is mounted on the frame moves — the grip positions and the tube
 * connections right behind them (see `getNodesMountedOnGripFrame`) — and all
 * of it by the same amount, so the cups keep their spacing. The rest of the
 * network stays put, so the tubes out to the tool are re-measured after the
 * move like after any other node move.
 *
 * Non-undoable, like `setNodePosition`: moving things around the canvas is not
 * something the undo history records today.
 */
var moveGripFrame = function (state, action) {
    var _a = action.payload.delta, x = _a.x, z = _a.z;
    if (!x && !z)
        return;
    getNodesMountedOnGripFrame(state).forEach(function (node) {
        node.position.x += x;
        node.position.z += z;
    });
};
/*
 * Turn the frame a quarter turn about its centre: clockwise from the starting
 * position, and back again from the turned one (see `gripFrameTurnDirection`).
 * Each cup is carried round to its new place and turned along with the frame,
 * so an oval cup keeps lying the same way across the frame. Like a move, only
 * what is mounted on the frame is affected (cups and the tube connections
 * right behind them, which turn too so their ports still face the cups).
 *
 * The frame's centre is a quarter turn's fixed point, and its outline turns
 * with the cups, so turning back lands every cup where it started.
 *
 * Non-undoable, like `moveGripFrame`: the turn back is the button itself.
 */
var turnGripFrame = function (state) {
    var frame = computeGripFrame(state.nodes);
    if (!frame)
        return;
    var direction = gripFrameTurnDirection(Boolean(state.gripFrameTurned));
    getNodesMountedOnGripFrame(state).forEach(function (node) {
        var turned = turnAboutGripFrameCentre(node.position, frame.center, direction);
        node.position.x = turned.x;
        node.position.z = turned.z;
        node.rotationY += gripFrameTurnDegrees(direction);
    });
    state.gripFrameTurned = direction === "clockwise";
};
var setGripPositionMarker = function (state, action) {
    var component = state.nodes[action.payload.nodeId]
        .component;
    component.marker = action.payload.marker;
    component.width = gripPositionMarkerDefaultWidth[action.payload.marker];
    component.height =
        gripPositionMarkerDefaultWidth[action.payload.marker] /
            defaultOvalGripPositionDiameterRatio;
};
var setGripPositionWidth = function (state, action) {
    var node = state.nodes[action.payload.nodeId];
    if (!node)
        return;
    node.component.width = action.payload.width;
};
var setGripPositionHeight = function (state, action) {
    var node = state.nodes[action.payload.nodeId];
    if (!node)
        return;
    node.component.height = action.payload.height;
};
var setGripPositionTubeLength = function (state, action) {
    var component = state.nodes[action.payload.nodeId]
        .component;
    component.inlineTubeLength = action.payload.length;
};
var setGripPositionTubeDiameter = function (state, action) {
    var component = state.nodes[action.payload.nodeId]
        .component;
    component.inlineTubeDiameter = action.payload.diameter;
};
var setNodeRotation = function (state, action) {
    state.nodes[action.payload.nodeId].rotationY = action.payload.rotation;
};
var setTubeConnectionDiameters = function (state, action) {
    var e_2, _a;
    try {
        for (var _b = __values(action.payload.nodeIds), _c = _b.next(); !_c.done; _c = _b.next()) {
            var nodeId = _c.value;
            var component = state.nodes[nodeId].component;
            component.diameters = __spreadArray([], __read(action.payload.diameters), false);
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_2) throw e_2.error; }
    }
};
var setTubeConnectionType = function (state, action) {
    var e_3, _a;
    try {
        for (var _b = __values(action.payload.nodeIds), _c = _b.next(); !_c.done; _c = _b.next()) {
            var nodeId = _c.value;
            var tubeConnectionType = action.payload.type;
            var component = state.nodes[nodeId].component;
            component.type = tubeConnectionType;
            // If the new tubeConnection type is tool-changer, we set the current diameter to the largest of the diameters.
            // Since the toolchanger only needs one diameter.
            if (tubeConnectionModels[tubeConnectionType].type === "tool-changer") {
                component.diameters = [Math.max.apply(Math, __spreadArray([], __read(component.diameters), false))];
            }
        }
    }
    catch (e_3_1) { e_3 = { error: e_3_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_3) throw e_3.error; }
    }
};
/*
 * A copy of a node at the given X/Z on the source's own plane: same kind,
 * rotation and component settings, but unconnected — the copy's tubes are the
 * user's to route.
 */
var copyNodeTo = function (state, fromNodeId, toNodeId, position) {
    var source = state.nodes[fromNodeId];
    state.nodes[toNodeId] = __assign(__assign({}, source), { id: toNodeId, position: { x: position.x, y: source.position.y, z: position.z }, component: __assign(__assign({}, source.component), { nodeId: toNodeId }), attachmentIds: [] });
};
var duplicateNode = function (state, action) {
    var _a = action.payload, fromNodeId = _a.fromNodeId, toNodeId = _a.toNodeId;
    var source = state.nodes[fromNodeId];
    if (source.type === NodeComponentType.FEED_PRESSURE_POINT)
        return;
    copyNodeTo(state, fromNodeId, toNodeId, {
        x: source.position.x + 0.1,
        z: source.position.z,
    });
};
/*
 * Several copies of a grip position at chosen places, as ONE undoable step —
 * the "fill an area with cups" tool. Undoing a fill removes every cup it made,
 * not one at a time.
 */
var duplicateNodeToPositions = function (state, action) {
    var _a = action.payload, fromNodeId = _a.fromNodeId, copies = _a.copies;
    var source = state.nodes[fromNodeId];
    if (!source || source.type !== NodeComponentType.GRIP_POSITION)
        return;
    copies.forEach(function (_a) {
        var toNodeId = _a.toNodeId, position = _a.position;
        return copyNodeTo(state, fromNodeId, toNodeId, position);
    });
};
var deleteEdge = function (state, action) {
    if (removeEdge(state, action.payload))
        updateTubeTypes(state);
};
var deleteAllEdges = function (state) {
    var clearAttachment = function (attachmentId) {
        var attachment = state.attachments[attachmentId];
        attachment.edgeId = undefined;
    };
    Object.values(state.edges).forEach(function (edge) {
        clearAttachment(edge.fromAttachmentId);
        clearAttachment(edge.toAttachmentId);
    });
    state.edges = {};
    /* Make all tube connector tube types unknown */
    updateTubeTypes(state);
};
var setEdgeDiameterBlowOffTube = function (state, action) {
    var e_4, _a;
    try {
        for (var _b = __values(action.payload.edgeIds), _c = _b.next(); !_c.done; _c = _b.next()) {
            var edgeId = _c.value;
            state.edges[edgeId].diameterBlowOffTube = action.payload.diameter;
            state.edges[edgeId].blowOffDiameterIndex = action.payload.index;
        }
    }
    catch (e_4_1) { e_4 = { error: e_4_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_4) throw e_4.error; }
    }
};
var setEdgeDiameterDefaultTube = function (state, action) {
    var e_5, _a;
    try {
        for (var _b = __values(action.payload.edgeIds), _c = _b.next(); !_c.done; _c = _b.next()) {
            var edgeId = _c.value;
            state.edges[edgeId].diameterDefaultTube = action.payload.diameter;
            state.edges[edgeId].defaultDiameterIndex = action.payload.index;
        }
    }
    catch (e_5_1) { e_5 = { error: e_5_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_5) throw e_5.error; }
    }
};
var setEdgeLengthLocked = function (state, action) {
    var e_6, _a;
    try {
        for (var _b = __values(action.payload.edgeIds), _c = _b.next(); !_c.done; _c = _b.next()) {
            var edgeId = _c.value;
            state.edges[edgeId].lengthLocked = action.payload.isLocked;
        }
    }
    catch (e_6_1) { e_6 = { error: e_6_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_6) throw e_6.error; }
    }
};
var setEdgeLength = function (state, action) {
    var e_7, _a;
    try {
        for (var _b = __values(action.payload.edgeIds), _c = _b.next(); !_c.done; _c = _b.next()) {
            var edgeId = _c.value;
            /* when the type of a Tube Connection is changed, tubes might get removed if there are less
             tube slots on the new TC. Then this function might get called with a edgeId of a removed tube */
            if (!state.edges[edgeId])
                continue;
            state.edges[edgeId].length = action.payload.length;
            state.edges[edgeId].lengthLocked = action.payload.isLocked;
        }
    }
    catch (e_7_1) { e_7 = { error: e_7_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_7) throw e_7.error; }
    }
};
var setObjectShape = function (state, action) {
    var e_8, _a;
    var objectShapeData = objectShapes[action.payload];
    state.objectShape = action.payload;
    if (objectShapeData.objectDefaultSize) {
        state.objectSize = objectShapeData.objectDefaultSize;
    }
    try {
        for (var _b = __values(Object.values(state.nodes)), _c = _b.next(); !_c.done; _c = _b.next()) {
            var node = _c.value;
            if (node.type === NodeComponentType.GRIP_POSITION) {
                node.position.y = objectShapeData.gripPositionY;
            }
            else {
                node.position.y = objectShapeData.tubeNetworkY;
            }
        }
    }
    catch (e_8_1) { e_8 = { error: e_8_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_8) throw e_8.error; }
    }
};
var setObjectSize = function (state, action) {
    state.objectSize = action.payload;
};
// Set the pallet footprint the cardboard boxes are meant to sit on. Callable
// (non-undoable) like `setObjectSize` since it's a live-edited dimension.
var setPalletSize = function (state, action) {
    state.pallet = action.payload;
};
/* A layer that is edited by hand no longer matches any pattern, so it becomes
 * "custom" (null) and is left alone by the automatic re-layout. Without this a
 * dragged or rotated box would be silently moved back the next time the box or
 * pallet size changed. */
var markLayerAsCustom = function (state, layer) {
    state.layerPatterns[layer] = null;
};
/*
 * Lay a stacking row out in a palletizing pattern, replacing whatever boxes it
 * held. The number of boxes is a result of the pattern, the transform and the
 * box/pallet footprints — see `computePalletPattern`.
 *
 * Box ids are derived from the row and the position's index rather than being
 * random, so laying the same pattern out again (after a size change) reuses the
 * same ids and the cup-to-box assignments and per-box forces keyed by them stay
 * pointing at the same boxes.
 */
var stampLayer = function (state, layer, pattern) {
    var placements = computePalletPattern(pattern.patternId, getBoxWorldFootprint(state.objectSize), state.pallet, objectPosition, pattern.transform);
    var boxes = placements.map(function (placement, index) { return ({
        id: "cardboard-box-l".concat(layer, "-").concat(index),
        position: placement.position,
        rotation: placement.rotation,
        layer: layer,
    }); });
    /* Laying out the same pattern at the same size again is a no-op. Bailing out
     * keeps the state (and with it the 3D entities and the cup-over-box raycast)
     * untouched, which matters because the automatic re-layout runs on any size
     * edit — including ones that don't change the footprint at all. */
    var currentBoxes = state.cardboardBoxes.filter(function (box) { return box.layer === layer; });
    var isUnchanged = currentBoxes.length === boxes.length &&
        currentBoxes.every(function (box, index) {
            var next = boxes[index];
            return (box.id === next.id &&
                box.size === undefined &&
                box.rotation === next.rotation &&
                box.position.x === next.position.x &&
                box.position.z === next.position.z);
        });
    if (isUnchanged)
        return;
    state.cardboardBoxes = __spreadArray(__spreadArray([], __read(state.cardboardBoxes.filter(function (box) { return box.layer !== layer; })), false), __read(boxes), false);
};
/* The layer is no longer the saved layer it was laid out from or saved as. */
var forgetSavedPalletLayout = function (state, layer) {
    if (state.savedPalletLayoutIds)
        delete state.savedPalletLayoutIds[layer];
};
/*
 * Apply a pattern to a row: record it and lay the row's boxes out in it. A
 * picked preset starts the row afresh, so saving it no longer offers to
 * overwrite the saved layer it was laid out from.
 */
var applyPatternToLayer = function (state, action) {
    var _a = action.payload, layer = _a.layer, patternId = _a.patternId, transform = _a.transform;
    state.layerPatterns[layer] = { patternId: patternId, transform: transform };
    stampLayer(state, layer, { patternId: patternId, transform: transform });
    forgetSavedPalletLayout(state, layer);
};
/*
 * Lay a row's boxes out again for the pattern it already has, without changing
 * what that pattern is. This is what a box/pallet size change needs: the
 * arrangement is unchanged, only the footprint it is built from moved.
 */
var restampLayerPattern = function (state, action) {
    var pattern = state.layerPatterns[action.payload.layer];
    if (!pattern)
        return;
    stampLayer(state, action.payload.layer, pattern);
};
/*
 * Bring the second stacking layer into use. A system has one layer until the
 * user asks for a second (see `selectLayersInUse`), so this is the "add layer"
 * gesture: when the first layer is laid out by a pattern, the second starts as
 * that pattern MIRRORED — the interlocking default a real stack wants
 * (identical layers are what makes a stack fall over). Some patterns are
 * symmetric (a uniform block grid mirrors onto itself); those start untransformed
 * so the stored pattern matches one the preset list actually offers. A
 * hand-arranged first layer has no pattern to copy, so its boxes are copied
 * instead and the new layer starts custom, for the user to rearrange.
 */
var addSecondLayer = function (state) {
    var firstLayerPattern = state.layerPatterns[0];
    // A new layer, even when it starts as a copy of the first.
    forgetSavedPalletLayout(state, 1);
    if (firstLayerPattern) {
        var boxFootprint_1 = getBoxWorldFootprint(state.objectSize);
        var layout = function (transform) {
            return computePalletPattern(firstLayerPattern.patternId, boxFootprint_1, state.pallet, objectPosition, transform);
        };
        var mirrorDiffers = !arePlacementsEquivalent(layout(PatternTransform.MIRROR), layout(PatternTransform.NONE), boxFootprint_1);
        var pattern = {
            patternId: firstLayerPattern.patternId,
            transform: mirrorDiffers
                ? PatternTransform.MIRROR
                : PatternTransform.NONE,
        };
        state.layerPatterns[1] = pattern;
        stampLayer(state, 1, pattern);
        return;
    }
    state.layerPatterns[1] = null;
    state.cardboardBoxes = __spreadArray(__spreadArray([], __read(state.cardboardBoxes.filter(function (box) { return box.layer !== 1; })), false), __read(state.cardboardBoxes
        .filter(function (box) { return box.layer === 0; })
        .map(function (box, index) { return (__assign(__assign({}, box), { id: "cardboard-box-l1-copy-".concat(index), layer: 1 })); })), false);
};
/*
 * Take the second stacking layer out of use again: its boxes and pattern are
 * removed and the system is single-layer until the next `addSecondLayer`.
 */
var removeSecondLayer = function (state) {
    state.cardboardBoxes = state.cardboardBoxes.filter(function (box) { return box.layer !== 1; });
    state.layerPatterns[1] = null;
    forgetSavedPalletLayout(state, 1);
};
// Cardboard boxes are compared by footprint only — the height is a constant
// the user never edits.
var hasSameFootprint = function (a, b) { return a.x === b.x && a.z === b.z; };
/*
 * Turn working with a pallet of boxes on or off for this system. The multi-box
 * UI (pallet, palletizing presets, stacking layers, per-box controls) only
 * exists while this is on.
 *
 * Turning it OFF collapses the scene back to the single centred box — the
 * second layer is removed and the first is laid out as the single-box pattern —
 * so the system genuinely looks and calculates like any other application
 * again, rather than merely hiding the controls over a still-filled pallet.
 * That makes the switch destructive to a built-up arrangement, which is why it
 * is undoable.
 *
 * The box size follows the switch, but only while it is still a default: a
 * single box is the 1 m box of every other application, a pallet is filled
 * with the smaller `palletBoxDefaultSize` so the patterns have room to differ.
 * A size the user typed in is theirs and is left alone in both directions.
 */
var setMultipleBoxesEnabled = function (state, action) {
    var _a;
    if (state.multipleBoxesEnabled === action.payload)
        return;
    state.multipleBoxesEnabled = action.payload;
    var singleBoxDefault = (_a = objectShapes[state.objectShape].objectDefaultSize) !== null && _a !== void 0 ? _a : defaultObjectSize;
    var _b = __read(action.payload
        ? [singleBoxDefault, palletBoxDefaultSize]
        : [palletBoxDefaultSize, singleBoxDefault], 2), leavingDefault = _b[0], enteringDefault = _b[1];
    if (hasSameFootprint(state.objectSize, leavingDefault)) {
        state.objectSize = __assign({}, enteringDefault);
    }
    if (action.payload)
        return;
    state.savedPalletLayoutIds = undefined;
    state.cardboardBoxes = state.cardboardBoxes.filter(function (box) { return box.layer !== 1; });
    state.layerPatterns[1] = null;
    var singleBox = {
        patternId: PalletPatternId.SINGLE,
        transform: PatternTransform.NONE,
    };
    state.layerPatterns[0] = singleBox;
    stampLayer(state, 0, singleBox);
};
// Add a box to the given layer. The layer is supplied by the caller (the
// `addCardboardBox` thunk in store/actions.ts reads the active layer) so the
// reducer stays pure and undoable.
var addCardboardBox = function (state, action) {
    var layer = action.payload.layer;
    // Drop the box in the first free spot, searched outward in a spiral from the
    // object origin so it lands as close to the layer's existing boxes as
    // possible without overlapping them.
    var boxesInLayer = state.cardboardBoxes.filter(function (b) { return b.layer === layer; });
    var position = findFreeCardboardBoxPosition(boxesInLayer, getBoxWorldFootprint(state.objectSize), objectPosition, function (box) { return getCardboardBoxWorldFootprint(box, state.objectSize); });
    state.cardboardBoxes.push({
        id: "cardboard-box-".concat(uuidv4()),
        position: position,
        rotation: 0,
        layer: layer,
    });
    markLayerAsCustom(state, layer);
};
// Duplicate an existing box, copying its rotation, size and layer and offsetting
// the position slightly so the copy doesn't perfectly overlap the original.
var duplicateCardboardBox = function (state, action) {
    var source = state.cardboardBoxes.find(function (b) { return b.id === action.payload; });
    if (!source)
        return;
    state.cardboardBoxes.push(__assign({ id: "cardboard-box-".concat(uuidv4()), position: {
            x: source.position.x + newCardboardBoxOffset.x,
            z: source.position.z + newCardboardBoxOffset.z,
        }, rotation: source.rotation, layer: source.layer }, (source.size && { size: __assign({}, source.size) })));
    markLayerAsCustom(state, source.layer);
};
// Rotate a box by 90 degrees (clockwise), cycling 0 → 90 → 180 → 270 → 0.
var rotateCardboardBox = function (state, action) {
    var box = state.cardboardBoxes.find(function (b) { return b.id === action.payload; });
    if (box) {
        box.rotation = (box.rotation + 90) % 360;
        markLayerAsCustom(state, box.layer);
    }
};
var removeCardboardBox = function (state, action) {
    var box = state.cardboardBoxes.find(function (b) { return b.id === action.payload; });
    if (!box)
        return;
    // Never allow removing the last box of its layer (a layer in use keeps at
    // least one box; an as-yet-unused layer may legitimately be empty).
    var boxesInLayer = state.cardboardBoxes.filter(function (b) { return b.layer === box.layer; });
    if (boxesInLayer.length <= 1)
        return;
    state.cardboardBoxes = state.cardboardBoxes.filter(function (b) { return b.id !== action.payload; });
    markLayerAsCustom(state, box.layer);
};
/*
 * Give one box a length/width of its own (`objectSize` axes), or hand it back
 * the shared size with `size: undefined`. Callable (non-undoable) like
 * `setObjectSize`: the size inputs dispatch on every keystroke.
 *
 * A pattern tiles one box size, so a box of a different size makes its layer
 * hand-arranged — otherwise the next re-layout would quietly drop the size.
 */
var setCardboardBoxSize = function (state, action) {
    var _a, _b;
    var box = state.cardboardBoxes.find(function (b) { return b.id === action.payload.id; });
    if (!box)
        return;
    var size = action.payload.size;
    if (((_a = box.size) === null || _a === void 0 ? void 0 : _a.x) === (size === null || size === void 0 ? void 0 : size.x) && ((_b = box.size) === null || _b === void 0 ? void 0 : _b.z) === (size === null || size === void 0 ? void 0 : size.z))
        return;
    if (size) {
        box.size = { x: size.x, z: size.z };
        markLayerAsCustom(state, box.layer);
    }
    else {
        delete box.size;
    }
};
/*
 * Undoable, unlike `setNodePosition`. A filtered action still changes `present`
 * without recording a step, so undoing right after one reverts it *together
 * with* the previous recorded step (e.g. the last added grip position). A box
 * drag dispatches once, on release, so recording it costs one step per drag.
 */
var setCardboardBoxPosition = function (state, action) {
    var box = state.cardboardBoxes.find(function (b) { return b.id === action.payload.id; });
    if (!box)
        return;
    var position = action.payload.position;
    /* A drag that ends where it started leaves the layout as the pattern laid it
     * out, so it must not count as a hand-made one. */
    if (box.position.x === position.x && box.position.z === position.z)
        return;
    box.position = position;
    markLayerAsCustom(state, box.layer);
};
/*
 * Lay a saved layer (see `store/scene/pallet-layout`) out on the given layer in
 * one undoable step: its boxes replace that layer's, which becomes
 * hand-arranged, and the system works with a pallet from then on. The pallet
 * and box size are the system's and stay as they are, and so does the other
 * layer. The cups stay where they are; which box each one is on follows from
 * the geometry.
 */
var applyPalletLayout = function (state, action) {
    var _a;
    var _b = action.payload, layer = _b.layer, layout = _b.layout, savedPalletLayoutId = _b.savedPalletLayoutId;
    state.cardboardBoxes = __spreadArray(__spreadArray([], __read(state.cardboardBoxes.filter(function (box) { return box.layer !== layer; })), false), __read(palletLayoutToBoxes(layout, layer)), false);
    state.layerPatterns[layer] = null;
    state.multipleBoxesEnabled = true;
    state.savedPalletLayoutIds = __assign(__assign({}, state.savedPalletLayoutIds), (_a = {}, _a[layer] = savedPalletLayoutId, _a));
};
/*
 * Remember which saved layer a layer was just saved as. Callable
 * (non-undoable): saving changes nothing in the design itself.
 */
var setSavedPalletLayoutId = function (state, action) {
    var _a;
    var _b = action.payload, layer = _b.layer, savedPalletLayoutId = _b.savedPalletLayoutId;
    state.savedPalletLayoutIds = __assign(__assign({}, state.savedPalletLayoutIds), (_a = {}, _a[layer] = savedPalletLayoutId, _a));
};
var clearScene = function (state) {
    state.nodes = initialState.nodes;
    state.edges = initialState.edges;
    state.attachments = initialState.attachments;
    // The next tool built starts out facing the default way.
    state.gripFrameTurned = undefined;
    // A new system starts without saved layers to overwrite.
    state.savedPalletLayoutIds = undefined;
};
var sceneSlice = createSlice({
    name: "scene",
    initialState: initialState,
    reducers: {
        addGripPosition: addGripPosition,
        addTubeConnection: addTubeConnection,
        addEdge: addEdge,
        addEdges: addEdges,
        addCentralizedPump: addCentralizedPump,
        deleteNode: deleteNode,
        deleteEntities: deleteEntities,
        duplicateNode: duplicateNode,
        duplicateNodeToPositions: duplicateNodeToPositions,
        setNodeAttachments: setNodeAttachments,
        setNodePosition: setNodePosition,
        moveGripFrame: moveGripFrame,
        turnGripFrame: turnGripFrame,
        setGripPositionMarker: setGripPositionMarker,
        setGripPositionWidth: setGripPositionWidth,
        setGripPositionHeight: setGripPositionHeight,
        setGripPositionTubeLength: setGripPositionTubeLength,
        setGripPositionTubeDiameter: setGripPositionTubeDiameter,
        setNodeRotation: setNodeRotation,
        setTubeConnectionType: setTubeConnectionType,
        setTubeConnectionDiameters: setTubeConnectionDiameters,
        deleteEdge: deleteEdge,
        deleteAllEdges: deleteAllEdges,
        setEdgeDiameterBlowOffTube: setEdgeDiameterBlowOffTube,
        setEdgeDiameterDefaultTube: setEdgeDiameterDefaultTube,
        setEdgeLength: setEdgeLength,
        setEdgeLengthLocked: setEdgeLengthLocked,
        setObjectShape: setObjectShape,
        setObjectSize: setObjectSize,
        setPalletSize: setPalletSize,
        /* Two reducers for the two halves of "arrange this row": picking a pattern
         * is a deliberate edit and belongs in the undo history, while the automatic
         * re-layout after a size change does not (undoing it would restore boxes
         * laid out for a size the system no longer has) and must leave the row's
         * pattern entry untouched. */
        applyPalletPattern: applyPatternToLayer,
        restampPalletPattern: restampLayerPattern,
        addSecondLayer: addSecondLayer,
        removeSecondLayer: removeSecondLayer,
        setMultipleBoxesEnabled: setMultipleBoxesEnabled,
        addCardboardBox: addCardboardBox,
        duplicateCardboardBox: duplicateCardboardBox,
        removeCardboardBox: removeCardboardBox,
        rotateCardboardBox: rotateCardboardBox,
        setCardboardBoxPosition: setCardboardBoxPosition,
        setCardboardBoxSize: setCardboardBoxSize,
        applyPalletLayout: applyPalletLayout,
        setSavedPalletLayoutId: setSavedPalletLayoutId,
        clearScene: clearScene,
    },
});
var allActions = sceneSlice.actions, reducer = sceneSlice.reducer;
export var callableActions = {
    setNodeAttachments: allActions.setNodeAttachments,
    setGripPositionMarker: allActions.setGripPositionMarker,
    setGripPositionWidth: allActions.setGripPositionWidth,
    setGripPositionHeight: allActions.setGripPositionHeight,
    setGripPositionTubeLength: allActions.setGripPositionTubeLength,
    setGripPositionTubeDiameter: allActions.setGripPositionTubeDiameter,
    setNodeRotation: allActions.setNodeRotation,
    setNodePosition: allActions.setNodePosition,
    moveGripFrame: allActions.moveGripFrame,
    turnGripFrame: allActions.turnGripFrame,
    setTubeConnectionType: allActions.setTubeConnectionType,
    setTubeConnectionDiameters: allActions.setTubeConnectionDiameters,
    setEdgeDiameterDefaultTube: allActions.setEdgeDiameterDefaultTube,
    setEdgeDiameterBlowOffTube: allActions.setEdgeDiameterBlowOffTube,
    setEdgeLength: allActions.setEdgeLength,
    setEdgeLengthLocked: allActions.setEdgeLengthLocked,
    setObjectSize: allActions.setObjectSize,
    setObjectShape: allActions.setObjectShape,
    setPalletSize: allActions.setPalletSize,
    restampPalletPattern: allActions.restampPalletPattern,
    setCardboardBoxSize: allActions.setCardboardBoxSize,
    setSavedPalletLayoutId: allActions.setSavedPalletLayoutId,
    deleteAllEdges: allActions.deleteAllEdges,
    clearScene: allActions.clearScene,
};
/* IMPORTANT: All of these actions will be added to the undo history.
 * Only add actions that we want to be able to undo here. */
export var internalActions = {
    addGripPosition: allActions.addGripPosition,
    addTubeConnection: allActions.addTubeConnection,
    addCentralizedPump: allActions.addCentralizedPump,
    addEdge: allActions.addEdge,
    addEdges: allActions.addEdges,
    deleteNode: allActions.deleteNode,
    deleteEntities: allActions.deleteEntities,
    duplicateNode: allActions.duplicateNode,
    duplicateNodeToPositions: allActions.duplicateNodeToPositions,
    deleteEdge: allActions.deleteEdge,
    applyPalletPattern: allActions.applyPalletPattern,
    addSecondLayer: allActions.addSecondLayer,
    removeSecondLayer: allActions.removeSecondLayer,
    setMultipleBoxesEnabled: allActions.setMultipleBoxesEnabled,
    addCardboardBox: allActions.addCardboardBox,
    duplicateCardboardBox: allActions.duplicateCardboardBox,
    removeCardboardBox: allActions.removeCardboardBox,
    rotateCardboardBox: allActions.rotateCardboardBox,
    setCardboardBoxPosition: allActions.setCardboardBoxPosition,
    applyPalletLayout: allActions.applyPalletLayout,
};
var options = {
    limit: 20,
    filter: includeAction(Object.values(internalActions).map(function (action) { return action.type; })),
    syncFilter: true,
    ignoreInitialState: true,
};
var undoableSceneReducer = undoable(reducer, options);
export default undoableSceneReducer;
